// src/layouts/AccordionSidebar.jsx
// Sidebar 1 cấp dạng accordion: gộp module (group) + chức năng (sub) vào cùng một cột.

import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, LogOut, KeyRound, UserRound } from "lucide-react";
import submenu, { moduleMeta } from "./submenuConfig";
import { menuItemHasReadAccess } from "../utils/permissionUtils";
import defaultAvatar from "../assets/images/avatar/employee-1.png";
import logo from "../assets/images/logo/logo-ima-hec.png";
import ChangePasswordModal from "../components/ChangePasswordModal";
import UserProfileModal from "../modules/hrm/profile/UserProfileModal";

export default function AccordionSidebar({
  activeGroup,
  activeSub,
  onSelect,
  onLogout,
  user,
  appVersion,
}) {
  const [openGroup, setOpenGroup] = useState(activeGroup || "");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showChangePass, setShowChangePass] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  // Điều hướng từ nơi khác (URL, redirect mặc định) thì mở đúng nhóm tương ứng
  useEffect(() => {
    if (activeGroup) setOpenGroup(activeGroup);
  }, [activeGroup]);

  const groups = useMemo(() => {
    const modules = JSON.parse(localStorage.getItem("modules") || "[]");
    const allowedIds = modules.map((m) => m.id);
    const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
    const isSuperAdmin =
      currentUser?.roles?.some((r) => r?.toLowerCase() === "super admin") || false;

    return moduleMeta.map((group) => {
      const visible = group.superOnly ? isSuperAdmin : allowedIds.includes(group.key);
      if (!visible) return null;

      const items = (submenu[group.key] || []).filter(
        (item) => item.enabled !== false && menuItemHasReadAccess(item)
      );
      if (items.length === 0) return null;

      return { ...group, items };
    }).filter(Boolean);
  }, []);

  const avatarUrl = user?.avatar_url || defaultAvatar;
  const branchName = user?.branch?.name || user?.branch?.label || "IMA Center";

  return (
    <div className="h-full w-64 bg-white border-r border-gray-100 flex flex-col">
      <div className="px-5 pt-5 pb-4 border-b border-gray-100">
        <img
          src={logo}
          alt="IMA Việt Nam"
          className="h-8 w-auto object-contain object-left"
        />
      </div>

      <div className="px-5 pt-4 pb-2">
        <span className="text-[11px] font-black uppercase tracking-[0.2em] text-gray-400">
          Menu
        </span>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
        {groups.map((group) => {
          const GroupIcon = group.icon;
          const isOpen = openGroup === group.key;
          const isActive = activeGroup === group.key;

          return (
            <div key={group.key}>
              <button
                onClick={() => setOpenGroup(isOpen ? "" : group.key)}
                className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold transition-colors ${
                  isActive
                    ? "bg-red-700 text-white shadow-md shadow-red-100"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <GroupIcon size={18} className="shrink-0" />
                <span className="flex-1 text-left">{group.label}</span>
                <ChevronDown
                  size={16}
                  className={`shrink-0 transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-1 space-y-0.5 pl-4">
                      {group.items.map((item) => {
                        const ItemIcon = item.icon;
                        const isCurrent =
                          activeGroup === group.key && activeSub === item.key;

                        return (
                          <button
                            key={item.key}
                            onClick={() => onSelect(group.key, item.key)}
                            className={`flex w-full items-center gap-3 rounded-lg border px-4 py-2 text-left text-[13px] transition-colors ${
                              isCurrent
                                ? "border-red-200 bg-red-50 font-semibold text-red-700"
                                : "border-transparent text-gray-600 hover:bg-gray-50 hover:text-red-700"
                            }`}
                          >
                            {ItemIcon && <ItemIcon size={16} className="shrink-0" />}
                            <span className="truncate">{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </nav>

      <div className="px-3 pb-4 space-y-3">
        <div className="rounded-xl border border-gray-100 py-2 text-center text-[11px] font-bold uppercase tracking-widest text-gray-400">
          IMA ERP {appVersion || "v0.0.0"}
        </div>

        <div className="relative">
          <AnimatePresence>
            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setUserMenuOpen(false)}
                />
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.96 }}
                  className="absolute bottom-full left-0 right-0 z-50 mb-2 overflow-hidden rounded-2xl border border-gray-100 bg-white py-2 shadow-2xl"
                >
                  <UserMenuBtn
                    icon={UserRound}
                    label="Tài khoản"
                    onClick={() => {
                      setShowProfile(true);
                      setUserMenuOpen(false);
                    }}
                  />
                  <UserMenuBtn
                    icon={KeyRound}
                    label="Đổi mật khẩu"
                    onClick={() => {
                      setShowChangePass(true);
                      setUserMenuOpen(false);
                    }}
                  />
                </motion.div>
              </>
            )}
          </AnimatePresence>

          <div className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-2.5">
            <button
              onClick={() => setUserMenuOpen((prev) => !prev)}
              className="flex min-w-0 flex-1 items-center gap-3 text-left"
            >
              <img
                src={avatarUrl}
                alt={user?.name || "User"}
                className="h-9 w-9 shrink-0 rounded-full object-cover"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-bold text-gray-800">
                  {user?.name || "Hệ thống"}
                </div>
                <div className="truncate text-[11px] text-gray-400">{branchName}</div>
              </div>
            </button>
            <button
              onClick={onLogout}
              title="Đăng xuất"
              className="shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </div>

      <ChangePasswordModal
        open={showChangePass}
        onClose={() => setShowChangePass(false)}
        user={user}
      />
      <UserProfileModal
        open={showProfile}
        onClose={() => setShowProfile(false)}
        userId={user?.id}
      />
    </div>
  );
}

const UserMenuBtn = ({ icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-[12px] font-bold text-gray-600 transition-colors hover:bg-gray-50 hover:text-red-700"
  >
    <Icon size={15} />
    {label}
  </button>
);
